// Teamverwaltung (design_handoff_team): Kopf, Bereichs-Segment und die drei Tabs Mitarbeiter, Teams, Sichtbarkeit.
// Fachlogik unverändert: Profile mit Rolle team/hausmeister nach Name, Archiv (#516), Löschen per RPC mit Rückfall.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, Plus, UserPlus } from '@phosphor-icons/react';
import { archivPatch, istArchiviert } from '../../../lib/team/archiv';
import { passtSuche, type TeamMitglied } from '../../../lib/team/mitarbeiter';
import { supabase } from '../../../lib/supabase/client';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { useViewport } from '../../../components/island/hooks';
import { useAppMeldungen } from '../../../components/ui/AppMeldungen';
import { GlasTooltipSchicht } from '../../../components/ui/GlasTooltip';
import { TeamsBereich, type TeamDialogZustand } from './teile/TeamsTeile';
import { SichtbarkeitBereich } from './teile/SichtbarkeitTeile';
import { TeamKopf, TeamSegment, type TeamTab } from './teile/Bausteine';
import { MitarbeiterListe } from './teile/MitarbeiterListe';
import { MitarbeiterDialog, MitarbeiterLoeschenDialog } from './teile/MitarbeiterDialoge';

const fehlerText = (err: unknown): string => (err instanceof Error ? err.message : (err as { message?: string } | null)?.message ?? String(err));

const TeamListe: React.FC = () => {
  const [team, setTeam] = useState<TeamMitglied[]>([]);
  const [activeTab, setActiveTab] = useState<TeamTab>('mitarbeiter');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Formular-Dialog: mitglied = null ⇒ anlegen
  const [formular, setFormular] = useState<{ mitglied: TeamMitglied | null } | null>(null);
  const [memberToDelete, setMemberToDelete] = useState<TeamMitglied | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // #516: Archivierte ausblenden (Umschalter neben der Suche)
  const [zeigeArchiv, setZeigeArchiv] = useState(false);
  const [anzahlTeams, setAnzahlTeams] = useState<number | undefined>(undefined);
  const [teamDialog, setTeamDialog] = useState<TeamDialogZustand>(null);

  const navigate = useNavigate();
  const { mobil, schmal } = useMobilModus();
  const { width } = useViewport();
  const { melde, bestaetige, dialoge } = useAppMeldungen(mobil);

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('profiles')
        .select('*')
        .in('role', ['team', 'hausmeister'])
        .order('full_name', { ascending: true });
      if (e) throw e;
      const teamData = (data || []) as TeamMitglied[];
      setTeam(teamData);
    } catch (err) {
      console.error('Error fetching team:', err);
      setError(fehlerText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchTeam(); }, [fetchTeam]);

  // Anzahl im Segment „Teams“ (nur Anzeige)
  useEffect(() => {
    void supabase.from('teams').select('id', { count: 'exact', head: true })
      .then(({ count }) => setAnzahlTeams(count ?? undefined));
  }, [activeTab]);

  const handleDelete = async () => {
    if (!memberToDelete) return;
    setIsDeleting(true);
    try {
      const { error: rpcError } = await supabase.rpc('delete_user_completely', { user_id: memberToDelete.id });
      if (rpcError) {
        const { error: profileError } = await supabase.from('profiles').delete().eq('id', memberToDelete.id);
        if (profileError) throw profileError;
      }
      setMemberToDelete(null);
      void fetchTeam();
    } catch (err) {
      melde('Fehler beim Löschen: ' + fehlerText(err), true);
    } finally {
      setIsDeleting(false);
    }
  };

  // #516: archivieren statt löschen – verschwindet aus allen Auswahllisten, Verlauf bleibt erhalten
  const archivieren = async (member: TeamMitglied, an: boolean) => {
    if (an && !(await bestaetige({
      nachricht: `${member.full_name || 'Mitarbeiter'} archivieren?\n\nDer Name verschwindet aus allen Auswahllisten (Zuständig, Kalender, Chat …). Bestehende Tickets, Zeiten und Termine bleiben erhalten. Die Anmeldung wird dadurch nicht gesperrt.`,
      label: 'Archivieren', icon: Archive, gefahr: false,
    }))) return;
    const { error: e } = await supabase.from('profiles').update(archivPatch(an)).eq('id', member.id);
    if (e) { melde('Fehler: ' + e.message, true); return; }
    void fetchTeam();
  };

  const anzahlArchiv = team.filter((m) => istArchiviert(m)).length;
  const filteredTeam = team.filter((m) => istArchiviert(m) === zeigeArchiv && passtSuche(m, searchTerm));

  const handleEditOpen = (member: TeamMitglied) => setFormular({ mitglied: member });

  const kopfAktion = activeTab === 'mitarbeiter' ? (
    <button type="button" onClick={() => setFormular({ mitglied: null })} data-tip="Mitarbeiter anlegen – Neuen Zugang mit Rolle und Passwort erstellen"
      className="inline-flex items-center"
      style={{ height: 44, padding: '0 20px', gap: 8, borderRadius: 22, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
        background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
      <UserPlus size={16} weight="bold" />Mitarbeiter anlegen
    </button>
  ) : activeTab === 'teams' ? (
    <button type="button" onClick={() => setTeamDialog({ team: null })} data-tip="Neues Team – Team anlegen und Teamleitung festlegen"
      className="inline-flex items-center"
      style={{ height: 44, padding: '0 20px', gap: 8, borderRadius: 22, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
        background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
      <Plus size={16} weight="bold" />Neues Team
    </button>
  ) : undefined;

  return (
    <>
      <div className="flex flex-col" style={{ gap: 14 }}>
        <TeamKopf mobil={mobil} schmal={schmal} aktion={kopfAktion} />
        <TeamSegment wert={activeTab} onChange={setActiveTab} mobil={mobil}
          anzahl={{ mitarbeiter: loading && team.length === 0 ? undefined : team.length - anzahlArchiv, teams: anzahlTeams }} />

        {activeTab === 'sichtbarkeit' ? (
          <SichtbarkeitBereich breit={width >= 1180} melde={melde} />
        ) : activeTab === 'teams' ? (
          <TeamsBereich dialog={teamDialog} setDialog={setTeamDialog} melde={melde} onAnzahl={setAnzahlTeams} />
        ) : (
          <MitarbeiterListe
            liste={filteredTeam} laedt={loading && team.length === 0} fehler={error} onErneut={() => void fetchTeam()}
            suche={searchTerm} onSuche={setSearchTerm}
            zeigeArchiv={zeigeArchiv} onZeigeArchiv={() => setZeigeArchiv((a) => !a)} anzahlArchiv={anzahlArchiv}
            tabelle={width >= 1180} mobil={mobil}
            onOeffnen={(m) => navigate(`/admin/team/${m.id}`)} onBearbeiten={handleEditOpen} onLoeschen={setMemberToDelete}
            onArchiv={(m, an) => void archivieren(m, an)}
          />
        )}
      </div>

      {formular && (
        <MitarbeiterDialog mitglied={formular.mitglied} onSchliessen={() => setFormular(null)} onErfolg={() => void fetchTeam()} />
      )}
      {memberToDelete && (
        <MitarbeiterLoeschenDialog name={memberToDelete.full_name} laeuft={isDeleting}
          onBestaetigen={() => void handleDelete()} onAbbrechen={() => setMemberToDelete(null)} />
      )}

      {dialoge}
      <GlasTooltipSchicht />
    </>
  );
};

export default TeamListe;
